import type { BookingRulesView, CancelReasonView } from "@planerius/contracts/cabinet"

import { formatDayMonth, formatTimeOfDay } from "../lib/format"
import { plural } from "../lib/plural"
import { formatDuration } from "./journal-time"
import { formatMoney } from "./money"

/**
 * Последствия отмены и неявки — СЛОВАМИ И ДО НАЖАТИЯ.
 *
 * Это главное требование ТЗ §2.9 и обоих диалогов макета: человек за стойкой обязан
 * увидеть, что именно произойдёт с клиентом, ПЕРЕД тем как нажать. «Отмена» и «неявка»
 * выглядят одинаково безобидно, а тянут за собой разное: счётчик человека, судьбу
 * предоплаты, карантин слота и закрытие онлайн-записи.
 *
 * ПОЧЕМУ ТЕКСТ СОБИРАЕТСЯ ЗДЕСЬ, А НЕ ЗАШИТ В РАЗМЕТКУ. Последствия считает НЕ кабинет —
 * их задают правила филиала (rules.*) и справочник причин (CancelReasonView). Салон,
 * у которого карантин выключен, а предоплата не сгорает, обязан увидеть свой текст,
 * а не текст из макета. Поэтому здесь ровно одна работа: превратить правила в русские
 * предложения. Ни одного числа отсюда не берётся «по умолчанию» — всё приходит с сервера.
 */

/**
 * «1 неявка», «2 неявки», «5 неявок». Без этого текст последствий звучит как робот.
 *
 * Правило склонения общее (`src/lib/plural.ts`); здесь остаётся только слово,
 * потому что «неявка» — понятие предметной области, а не языка.
 */
export function noShowWord(count: number): string {
  return plural(count, "неявка", "неявки", "неявок")
}

/**
 * Форма ПОСЛЕ ПРЕДЛОГА «при»: «при 1 неявке», «при 3 неявках», «при 21 неявке».
 *
 * Обычное склонение по числу здесь не годится: «при 3 неявки» — не по-русски, а порог
 * салона показывается именно в этой конструкции. Отдельная функция, потому что ошибку
 * такого рода замечают все, кроме кода.
 */
export function noShowWordAfterPreposition(count: number): string {
  const teen = count % 100 >= 11 && count % 100 <= 14
  return !teen && count % 10 === 1 ? "неявке" : "неявках"
}

export type ConsequenceTone = "client" | "salon" | "none"

export interface Consequence {
  readonly tone: ConsequenceTone
  readonly text: string
}

export interface CancelConsequenceInput {
  /** Кто отменяет. null — ещё не выбрано, и это ОТДЕЛЬНОЕ состояние, а не «салон». */
  readonly initiatedBy: "client" | "staff" | null
  readonly reason: CancelReasonView | null
  readonly rules: BookingRulesView
  /** Предоплата. null — деньги этому человеку не показываются: молчим про них вовсе. */
  readonly prepaidMinor: number | null
  readonly clientNoShowCount: number
  /** Сколько минут осталось до визита. Отрицательное — визит уже прошёл. */
  readonly minutesUntilVisit: number
  /** Есть ли исполнитель: без него занятости нет, и карантину нечего держать. */
  readonly hasExecutor: boolean
  readonly notifyClient: boolean
}

/**
 * Последствия отмены (2-2.9.2).
 *
 * Пока инициатор не выбран, последствий НЕТ — и это не «пустое место», а ответ:
 * от инициатора зависят счётчик и предоплата, поэтому значения по умолчанию у поля нет.
 */
export function cancelConsequence(input: CancelConsequenceInput): Consequence {
  if (input.initiatedBy === null) {
    return {
      tone: "none",
      text: "Последствия зависят от того, кто отменяет — выберите инициатора.",
    }
  }

  const parts: string[] = []
  const byClient = input.initiatedBy === "client"
  const reason = input.reason

  if (byClient) {
    if (reason?.countsAsNoShow === true) {
      const next = input.clientNoShowCount + 1
      parts.push(
        `Эта причина засчитывается как неявка: у клиента станет ${String(next)} ${noShowWord(next)}.`,
      )
    } else {
      parts.push("Отмена засчитается клиенту: это отмена, а не неявка.")
    }
  } else {
    parts.push("Клиенту это не засчитается.")
  }

  if (input.prepaidMinor !== null && input.prepaidMinor > 0) {
    const money = formatMoney(input.prepaidMinor)
    if (reason?.forfeitsPrepay === true) {
      const late =
        input.minutesUntilVisit < input.rules.cancelDeadlineMinutes
          ? ` — до визита меньше ${formatDuration(input.rules.cancelDeadlineMinutes)}`
          : ""
      parts.push(`Предоплата ${money} останется у салона${late}.`)
    } else {
      parts.push(`Предоплата ${money} вернётся клиенту.`)
    }
  }

  // КАРАНТИН СЧИТАЕТСЯ ТАК ЖЕ, КАК НА СЕРВЕРЕ, А НЕ ТОЛЬКО ПО ПРИЗНАКУ ПРИЧИНЫ.
  // BookingService ставит карантин, если инициатор — КЛИЕНТ, независимо от кода причины
  // (triggers_quarantine добавляет к этому отмены салона вроде «мастер заболел»).
  // Диалог, промолчавший про карантин, обещает администратору свободное время, которое
  // он тут же увидит занятым, — и позвонит в поддержку вместо того, чтобы его занять.
  //
  // Прошедший визит карантина не даёт: держать от онлайн-записи уже нечего (сервер
  // проверяет start_at в будущем), и обещать возврат времени было бы неправдой.
  const quarantined =
    input.rules.quarantineMinutes > 0 &&
    input.minutesUntilVisit > 0 &&
    input.hasExecutor &&
    (byClient || reason?.triggersQuarantine === true)
  if (quarantined) {
    parts.push(
      `Время вернётся в онлайн-запись через ${formatDuration(input.rules.quarantineMinutes)}, до этого его можно занять вручную.`,
    )
  }

  if (input.notifyClient) {
    parts.push(
      byClient
        ? "Клиенту уйдёт уведомление."
        : "Клиенту уйдёт уведомление — текст по шаблону салона.",
    )
  } else {
    parts.push("Уведомление клиенту не уйдёт — предупредите его сами.")
  }

  return { tone: byClient ? "client" : "salon", text: parts.join(" ") }
}

/**
 * «Клиент мог отменить сам до 13:30. Сейчас отмена только через вас» (5-А3).
 *
 * ЗАЧЕМ ЭТО В ДИАЛОГЕ. Администратор отменяет визит по звонку и не знает, почему клиент
 * не сделал этого сам в приложении: разговор начинается с «у вас не работает». Строка
 * отвечает на это до нажатия — срок салона прошёл, и отменить теперь может только стойка.
 *
 * null — срок ещё не прошёл или онлайн-отмена у салона выключена вовсе: придумывать
 * ограничение, которого нет, нельзя.
 */
export function lateCancelNote(input: {
  readonly startAt: string
  readonly cancelDeadlineMinutes: number
  readonly allowOnlineCancel: boolean
  readonly timeZone: string
  readonly nowMs: number
}): string | null {
  if (!input.allowOnlineCancel || input.cancelDeadlineMinutes <= 0) return null
  const start = Date.parse(input.startAt)
  if (Number.isNaN(start)) return null
  const deadline = start - input.cancelDeadlineMinutes * 60_000
  if (input.nowMs <= deadline) return null

  // ЧАС БЕЗ ДАТЫ ГОДИТСЯ ТОЛЬКО ВНУТРИ ОДНОГО ДНЯ. При сроке отмены в сутки и больше
  // «до 16:30» — это другой день, и администратор передаёт клиенту неправду, от которой
  // спор станет только хуже. Календарный день считается в зоне ФИЛИАЛА: браузер
  // администратора может стоять в другой.
  const sameDay =
    calendarDay(new Date(deadline), input.timeZone) === calendarDay(new Date(start), input.timeZone)
  const when = sameDay
    ? formatTimeOfDay(new Date(deadline), input.timeZone)
    : `${formatTimeOfDay(new Date(deadline), input.timeZone)}, ${formatDayMonth(new Date(deadline), input.timeZone)}`
  return `Клиент мог отменить сам до ${when}. Сейчас отмена только через вас.`
}

/** Календарный день момента в зоне филиала — «2026-09-15». */
function calendarDay(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, dateStyle: "short" }).format(at)
}

/**
 * Что говорят правила филиала о пороге неявок. Пустая строка — политика «ничего
 * не делать»: придумывать за салон последствие, которого он не включал, нельзя.
 */
export function noShowPolicyNote(rules: BookingRulesView): string {
  if (rules.noShowThreshold <= 0) return ""
  const threshold = `${String(rules.noShowThreshold)} ${noShowWordAfterPreposition(rules.noShowThreshold)}`
  switch (rules.noShowPolicy) {
    case "warn":
      return `По правилам салона при ${threshold} администратор получает предупреждение при записи.`
    case "require_prepay":
      return `По правилам салона при ${threshold} записать клиента можно только с предоплатой.`
    case "admin_only":
      return `По правилам салона при ${threshold} онлайн-запись клиенту закрывается — записать сможет только администратор.`
    case "block":
      // «Запись закрывается» без уточнения канала читается как admin_only, у которого
      // текст соседний. block запрещает запись целиком — и это надо сказать словами,
      // иначе администратор попробует записать человека сам и упрётся в отказ сервера.
      return `По правилам салона при ${threshold} записать клиента нельзя вовсе — ни онлайн, ни администратору.`
    case "none":
      return ""
    default:
      return ""
  }
}

export interface NoShowConsequenceInput {
  readonly rules: BookingRulesView
  readonly clientNoShowCount: number
  readonly prepaidMinor: number | null
}

/** Последствия отметки «не пришёл» (2-2.9.3). Репутационная отметка на живом человеке. */
export function noShowConsequence(input: NoShowConsequenceInput): string {
  const next = input.clientNoShowCount + 1
  const parts = [`У клиента станет ${String(next)} ${noShowWord(next)}.`]

  const policy = noShowPolicyNote(input.rules)
  if (policy !== "") parts.push(policy)

  if (input.prepaidMinor !== null && input.prepaidMinor > 0) {
    parts.push(`Предоплата ${formatMoney(input.prepaidMinor)} останется у салона.`)
  }

  return parts.join(" ")
}
